import React, { useState, useEffect, useContext, Fragment } from 'react';
import DeletionContext from 'src/MultiDeletionFunctionality/@state/DeletionStateContext';
import DELETE_ACTIONS from 'src/MultiDeletionFunctionality/@state_manager/actions';
import PropTypes from 'prop-types';
import {
  PowerTable,
  Pagination,
  MessageModal,
  ConfirmDelete,
  NoInformationFound,
} from 'Components';
import { confirmAAStatus } from 'helpers/confirmStatus';
import Fetch from 'helpers/fetch';
import i18n from 'i18next';
import { buildUrl } from 'helpers/misc';
import usePrevious from 'helpers/hooks/usePrevious';
import rowRules from 'helpers/rowRules';
import AppContext from 'src/AppContext';
import menuOptions from 'constants/menuOptions';
import { ADDITIONAL_ANALYSIS } from 'constants/apis';
import AdditionTablePopover from './AdditionTablePopover/AdditionTablePopover';

const AdditionTab = (props) => {
  const { deletionState, dispatch } = useContext(DeletionContext);

  const [state, setState] = useState({
    data: [],
    isLoading: true,
    totalItems: 0,
    currentPage: 1,
    sort: '-Id',
  });
  const [pageState, setPageState] = useState({ powerTableIndex: null });
  const { clientNumber, isInternalUser, setAppState } = useContext(AppContext);
  const [cancelItemState, setCancelItemState] = useState({
    cancelItemIndex: undefined,
  });

  const [deleteItemIndex, setDeleteItemIndex] = useState(undefined);
  const prevAdditionTabPageSize = usePrevious(props.additionTabPageSize);

  useEffect(() => {
    dispatch({
      type: DELETE_ACTIONS.UPDATE_DELETION_STATE,
      payload: {
        data: state.data,
        tableCode: 'additionTab',
        idName: 'Id',
        isCheckedAll: false,
      },
    });
    dispatch({ type: DELETE_ACTIONS.GENERATE_ALLOWED_ITEMS_LIST });
  }, [props.additionTabPageSize, state.isLoading,JSON.stringify(deletionState.data)]);


  useEffect(() => {
    if (prevAdditionTabPageSize !== props.additionTabPageSize) {
      setState((prev) => ({ ...prev, isLoading: true }));
    }
  }, [prevAdditionTabPageSize, props.additionTabPageSize]);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchAdditionalAnalysisData = (newAbortController) => {
      setState((prev) => ({ ...prev, data: [] }));
      Fetch.Get(
        buildUrl(ADDITIONAL_ANALYSIS, {
          ClientNumber: isInternalUser ? clientNumber : undefined,
          pageSize: props.additionTabPageSize,
          pageNumber: state.currentPage - 1,
          sort: state.sort,
          ...props.searchParams,
        }),
        { signal: newAbortController.signal },
      )
        .then((response) => response.json())
        .then((data) => {
          setState((prev) => ({
            ...prev,
            data: data.DataList,
            totalItems: data.MatchCriteriaCount,
            isLoading: false,
          }));
        })
        .catch(({ response }) => {
          if (response) {
            setState((prev) => ({
              ...prev,
              isLoading: false,
            }));
          }
        });
    };

    if (state.isLoading) {
      fetchAdditionalAnalysisData(abortController);
    }

    return () => {
      abortController.abort();
    };
  }, [
    props.additionTabPageSize,
    state.currentPage,
    isInternalUser,
    clientNumber,
    state.isLoading,
    props.searchParams,
  ]);

  const onOptionClick = (code, index) => {
    switch (code) {
      case 'view': {
        props.history.push(`/jobs/${state.data[index].MaxxamJobNumber}/`);
        break;
      }

      case 'share': {
        props.setAppState({
          shareId: state.data[index].Id,
          shareCode: 'AdditionalAnalysis',
        });

        break;
      }

      case 'edit': {
        setAppState({ isGlobalLoaderShowing: true });
        confirmAAStatus(state.data[index])
          .then(setState({ ...state, isLoading: true }))
          .then(() => {
            // Need to handle case when  all sample disposed
            const url=`${ADDITIONAL_ANALYSIS}/${encodeURIComponent(state.data[index].Id)}/EditStatus`;

            if (state.data[index].Status === 'S') {
              Fetch.Put(url, {
                Id: state.data[index].Id,
                Status: 'E',
              });
            }

            if (state.data[index].Status === 'C') {
              Fetch.Put(url, {
                Id: state.data[index].Id,
                Status: 'D',
              });
            }
            setAppState({ isGlobalLoaderShowing: false })
            props.history.push(
              `/additional-analysis/${state.data[index].Id}/edit`,
            );
          })
            .catch(({ response }) => {
                props.onAPIError(response, true)
                setAppState({ isGlobalLoaderShowing: false })
            })
        break;
      }

      case 'cancel': {
        const item = state.data[index];
        if (cancelItemState.cancelItemIndex !== undefined) {
          setPageState({ powerTableIndex: null });
          setAppState({ isGlobalLoaderShowing: true });

          setCancelItemState({
            cancelItemIndex: undefined,
          });

          confirmAAStatus(item).then(() => {
            const url=`${ADDITIONAL_ANALYSIS}/${encodeURIComponent(state.data[index].Id)}/EditStatus`;
            Fetch.Put(url, {
              Id: item.Id,
              Status: 'C',
            })
              .then(() => {
                setState({ ...state, isLoading: true });
              })
              .catch(({ response }) => props.onAPIError(response, true))
              .finally(() => setAppState({ isGlobalLoaderShowing: false }));
          });
        } else {
          setCancelItemState({
            cancelItemIndex: index,
          });
        }
        break;
      }

      case 'delete': {
        const item = state.data[index];
        setPageState({ powerTableIndex: null });
        if (deleteItemIndex !== undefined) {
          setAppState({ isGlobalLoaderShowing: true });
          setDeleteItemIndex(undefined);

          confirmAAStatus(item).then(() => {
            Fetch.Delete(ADDITIONAL_ANALYSIS, { IdList: [item.Id] })
              .then(() => {
                setState({ ...state, isLoading: true });
              })
              .catch(({ response }) => props.onAPIError(response, true))
              .finally(() =>
                setAppState(() => {
                  return { isGlobalLoaderShowing: false };
                }),
              );
          });
        } else {
          setDeleteItemIndex(index);
        }
        break;
      }

      // ! Multi deletion:
      case 'multiDelete':
        {
          const item = state.data[index];
          dispatch({
            type: DELETE_ACTIONS.ACTIVATE_DELETION_MODE,
            payload: { id: item.Id },
          });
        }
        break;

      default: {
        setPageState({ powerTableIndex: null });
        break;
      }
    }
  };

  const multiDeletionSubmission = () => {
    Fetch.Delete(ADDITIONAL_ANALYSIS, { IdList: deletionState.selectedItems })
      .then(() => {
        setState(() => {
          dispatch({ type: DELETE_ACTIONS.SUCCESSFUL_DELETION });
          return { ...state, isLoading: true };
        });
      })
      .catch(({ response }) => props.onAPIError(response, true))
      .finally(() =>
        setAppState(() => {
          dispatch({ type: DELETE_ACTIONS.RESET_DELETION_STATE });
          return { isGlobalLoaderShowing: false };
        }),
      );
  };

  if (!state.isLoading && state.data.length === 0) {
    return (
        <NoInformationFound titleText={i18n.t('additionalAnalysis:NoAdditionalAnalysis')}/>
    );
  }

  return (
    <Fragment>
      <PowerTable
        data={state.data}
        isLoading={state.isLoading}
        keys={props.additionTabKeyPairs.keys}
        keyPairs={props.additionTabKeyPairs.keyPairs}
        onOptionClick={onOptionClick}
        powerTableIndex={pageState.powerTableIndex}
        setPageState={setPageState}
        setAppState={props.setAppState}
        menuOptions={menuOptions}
        rowRules={rowRules.additionTab}
        sort={state.sort}
        handleSortClick={(newSort) =>
          setState((prev) => ({ ...prev, isLoading: true, sort: newSort }))
        }
        hasManualSort
        viewCode="additionTab"
        renderLeftDocumentPopover={({ Id }) => {
          return <AdditionTablePopover Id={Id} />;
        }}
      />
      <div className="jobs-desktop__pagination">
        <Pagination
          totalItems={state.totalItems}
          itemsPerPage={props.additionTabPageSize}
          currentPage={state.currentPage}
          onClick={(pageNumber) =>
            setState((prev) => ({
              ...prev,
              currentPage: pageNumber,
              isLoading: true,
            }))
          }
        />
      </div>

      {/* //! What is this for? */}
      {cancelItemState.cancelItemIndex !== undefined && (
        <MessageModal
          message={i18n.t('confirmModal:aaCancel')}
          firstButtonText={i18n.t('common:No')}
          onFirstButtonClick={() =>
            setCancelItemState({
              cancelComments: '',
              cancelItemIndex: undefined,
            })
          }
          secondButtonText={i18n.t('common:Yes')}
          onSecondButtonClick={() =>
            onOptionClick('cancel', cancelItemState.cancelItemIndex)
          }
        />
      )}

      {/* {deleteItemIndex !== undefined || (deletionState.isDeletionModeActive && deletionState.isDeleteConfirmationActive) && ( */}
      {deletionState.isDeletionModeActive &&
      deletionState.isDeleteConfirmationActive ? (
        <ConfirmDelete
          title={i18n.t('multiDeletion:MultiDeleteRecordsConfirmation')}
          onCancelDelete={() => {
            dispatch({ type: DELETE_ACTIONS.DEACTIVATE_DELETE_CONFIRMATION });
          }}
          onProceedDelete={() => {
            multiDeletionSubmission();
          }}
        />
      ) : (
        deleteItemIndex !== undefined && (
          <ConfirmDelete
            title={i18n.t('additionalAnalysis:deleteAdditionalConfirmation')}
            onCancelDelete={() => {
              setDeleteItemIndex(undefined);
            }}
            onProceedDelete={() => {
              onOptionClick('delete', deleteItemIndex);
            }}
          />
        )
      )}
    </Fragment>
  );
};

AdditionTab.propTypes = {
  setAppState: PropTypes.func,
  additionTabKeyPairs: PropTypes.shape({
    keys: PropTypes.arrayOf(PropTypes.string),
    keyPairs: PropTypes.shape({}),
  }),
  additionTabPageSize: PropTypes.number,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  onAPIError: PropTypes.func,
  searchParams: PropTypes.shape({}),
};

export default AdditionTab;



// WEBPACK FOOTER //
// ./src/containers/Jobs/AdditionTab/AdditionTab.jsx